/**
 * Generic API client for making requests to the backend.
 */

import { clearSessionAndRedirect } from './sessionManager';
import { captureException } from '../config/sentry.config';
import { API_URL, API_KEY } from '../config/appConfig';

// Custom error class for API-related errors
export class ApiError extends Error {
  constructor(message: string, public readonly status?: number, public readonly code?: string) {
    super(message);
    this.name = 'ApiError';
  }
}

// Specific error classes for authentication errors
export class UnauthorizedError extends ApiError {
  constructor(message: string) {
    super(message, 401, 'UNAUTHORIZED');
    this.name = 'UnauthorizedError';
  }
}

export class ForbiddenError extends ApiError {
  constructor(message: string) {
    super(message, 403, 'FORBIDDEN');
    this.name = 'ForbiddenError';
  }
}

interface ApiClientOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  headers?: Record<string, string>;
  body?: object;
  /**
   * When true, a 403 response throws ForbiddenError without clearing the session.
   * Use this for endpoints where 403 means "insufficient role" rather than "account suspended".
   */
  skipAuthErrorHandling?: boolean;
}

/**
 * Performs an API request.
 * @param endpoint The API endpoint to call (e.g., '/auth/is_first').
 * @param options The request options (method, headers, body).
 * @returns The JSON response from the API.
 * @throws {ApiError} if the request fails.
 */
export const apiClient = async <T>(endpoint: string, options: ApiClientOptions = {}): Promise<T> => {
  const { method = 'GET', headers = {}, body, skipAuthErrorHandling = false } = options;

  const defaultHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Add API key for chat service
  if (endpoint.startsWith('/assistant')) {
    defaultHeaders['API-KEY'] = API_KEY;
  }

  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method,
      headers: { ...defaultHeaders, ...headers },
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      // Intentamos parsear JSON, pero si falla devolvemos el texto crudo para debugging.
      let errorData: Record<string, unknown> = { message: `HTTP error! Status: ${response.status}` };
      try {
        errorData = await response.json();
      } catch {
        // No JSON - leer como texto
        const text = await response.text().catch(() => '<no body>');
        errorData = { message: text || `HTTP error! Status: ${response.status}`, rawText: text };
      }

      // FastAPI uses "detail", other services may use "message"
      const errorMessage = (
        typeof errorData === 'object' && errorData !== null
          ? ((errorData as Record<string, unknown>).message as string)
            || ((errorData as Record<string, unknown>).detail as string)
          : null
      ) || `HTTP error! Status: ${response.status}`;

      // Handle 401 Unauthorized - Session expired or invalid token
      if (response.status === 401) {
        const error = new UnauthorizedError(errorMessage);
        captureException(error, {
          endpoint,
          status: response.status,
          errorData,
        });
        console.error('[apiClient] 401 Unauthorized - Clearing session');
        clearSessionAndRedirect('Session expired or invalid (401 Unauthorized)');
        throw error;
      }

      // Handle 403 Forbidden - Access denied
      if (response.status === 403) {
        const error = new ForbiddenError(errorMessage);
        if (skipAuthErrorHandling) {
          // Caller will handle 403 — e.g. admin role check where 403 means "not an admin"
          throw error;
        }
        captureException(error, {
          endpoint,
          status: response.status,
          errorData,
        });
        console.error('[apiClient] 403 Forbidden - Clearing session');
        clearSessionAndRedirect('Access forbidden (403)');
        throw error;
      }

      // Log detallado para errores de servidor (5xx)
      if (response.status >= 500) {
        // En desarrollo, imprimir detalles en consola para facilitar debugging.
        // Nota: esto es seguro en entornos de desarrollo; en producción preferiría enviar a un logger.
        console.error('[apiClient] Server error response:', {
          endpoint,
          status: response.status,
          headers: Object.fromEntries(response.headers.entries()),
          body: errorData,
        });
      }

      throw new ApiError(
        errorMessage,
        response.status
      );
    }

    return await response.json() as T;

  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    // Handle network errors or other unexpected issues
    const networkError = new ApiError(
      error instanceof Error ? error.message : 'An unknown network error occurred',
      undefined,
      'NETWORK_ERROR'
    );

    // Capture network errors to Sentry
    captureException(error instanceof Error ? error : new Error('Network error'), {
      endpoint,
      errorType: 'network',
    });

    throw networkError;
  }
};
